import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { ServiceForm, ServiceFormData } from '../../components/vendor/ServiceForm';
export function Services() {
  const [services, setServices] = useState<ServiceFormData[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<ServiceFormData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
  // Load services from localStorage on component mount
  useEffect(() => {
    const storedServices = localStorage.getItem('vendorServices');
    if (storedServices) {
      setServices(JSON.parse(storedServices));
    } else {
      // Set some default services if none exist
      const defaultServices = [{
        id: '1',
        name: 'Standard Room Painting',
        category: 'painting',
        price: 450,
        description: 'Complete painting service for a standard room including walls and ceiling. Includes premium 0 VOC paint.',
        duration: '1-2 days',
        warranty: '2 years',
        isActive: true
      }, {
        id: '2',
        name: 'Bathroom Waterproofing',
        category: 'waterproofing',
        price: 750,
        description: 'Complete waterproofing solution for bathrooms to prevent water leakage and damage.',
        duration: '2-3 days',
        warranty: '1 year',
        isActive: true
      }, {
        id: '3',
        name: 'Door Repair',
        category: 'micro-repair',
        price: 180,
        description: 'Repair service for damaged doors including hinges, handles, and locks replacement.',
        duration: '2-4 hours',
        warranty: '3 months',
        isActive: true
      }];
      setServices(defaultServices);
      localStorage.setItem('vendorServices', JSON.stringify(defaultServices));
    }
  }, []);
  // Save services to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('vendorServices', JSON.stringify(services));
  }, [services]);
  const handleAddService = (data: ServiceFormData) => {
    const newService = {
      ...data,
      id: Date.now().toString()
    };
    setServices([...services, newService]);
    setIsFormOpen(false);
  };
  const handleUpdateService = (data: ServiceFormData) => {
    const updatedServices = services.map(service => service.id === data.id ? data : service);
    setServices(updatedServices);
    setEditingService(null);
  };
  const handleDeleteService = (id: string) => {
    setServiceToDelete(id);
    setIsDeleteModalOpen(true);
  };
  const confirmDeleteService = () => {
    if (serviceToDelete) {
      const updatedServices = services.filter(service => service.id !== serviceToDelete);
      setServices(updatedServices);
      setIsDeleteModalOpen(false);
      setServiceToDelete(null);
    }
  };
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'painting':
        return 'Painting';
      case 'waterproofing':
        return 'Waterproofing';
      case 'micro-repair':
        return 'Micro-repair';
      default:
        return category;
    }
  };
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'painting':
        return 'bg-blue-100 text-blue-800';
      case 'waterproofing':
        return 'bg-green-100 text-green-800';
      case 'micro-repair':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="light" data-id="element-2051">
      <div className="flex justify-between items-center mb-6" data-id="element-2052">
        <div data-id="element-2053">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2054">Services</h1>
          <p className="text-gray-600 mt-1" data-id="element-2055">Manage your service offerings</p>
        </div>
        <button onClick={() => {
        setEditingService(null);
        setIsFormOpen(true);
      }} className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-id="element-2056">
          <Plus size={18} className="mr-1" data-id="element-2057" />
          Add New Service
        </button>
      </div>
      {isFormOpen && !editingService && <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-2058">
          <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-2059">
            Add New Service
          </h2>
          <ServiceForm onSubmit={handleAddService} onCancel={() => setIsFormOpen(false)} data-id="element-2060" />
        </div>}
      {editingService && <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-2061">
          <h2 className="text-xl font-medium text-gray-900 mb-4" data-id="element-2062">
            Edit Service
          </h2>
          <ServiceForm defaultValues={editingService} onSubmit={handleUpdateService} onCancel={() => setEditingService(null)} data-id="element-2063" />
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-2064">
        <div className="overflow-x-auto" data-id="element-2065">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2066">
            <thead className="bg-gray-50" data-id="element-2067">
              <tr data-id="element-2068">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2069">
                  Service
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2070">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2071">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2072">
                  Duration
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2073">
                  Warranty
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2074">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2075">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2076">
              {services.length === 0 ? <tr data-id="element-2077">
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500" data-id="element-2078">
                    No services found. Click "Add New Service" to create one.
                  </td>
                </tr> : services.map(service => <tr key={service.id} className="hover:bg-gray-50" data-id="element-2079">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2080">
                      <div className="text-sm font-medium text-gray-900" data-id="element-2081">
                        {service.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2082">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getCategoryColor(service.category)}`} data-id="element-2083">
                        {getCategoryLabel(service.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-2084">
                      MYR {service.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-2085">
                      {service.duration}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-2086">
                      {service.warranty}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2087">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${service.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2088">
                        {service.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2089">
                      <button onClick={() => setEditingService(service)} className="text-primary hover:text-primary/80 mr-3" data-id="element-2090">
                        <Edit size={18} data-id="element-2091" />
                      </button>
                      <button onClick={() => handleDeleteService(service.id!)} className="text-red-600 hover:text-red-800" data-id="element-2092">
                        <Trash2 size={18} data-id="element-2093" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2094">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-2095">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-2096">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-2097"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-2098">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-2099">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-2100">
                <div className="sm:flex sm:items-start" data-id="element-2101">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-2102">
                    <AlertCircle className="h-6 w-6 text-red-600" data-id="element-2103" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-2104">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-2105">
                      Delete Service
                    </h3>
                    <div className="mt-2" data-id="element-2106">
                      <p className="text-sm text-gray-500" data-id="element-2107">
                        Are you sure you want to delete this service? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-2108">
                <button type="button" onClick={confirmDeleteService} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-2109">
                  Delete
                </button>
                <button type="button" onClick={() => setIsDeleteModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-2110">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}